import React, { useCallback, useEffect } from 'react';
import Assignments from 'components/Assignments';
import Spinner from 'components/Spinner';
import StopsDetails from 'components/StopsDetails';
import SideBar from 'components/SideBar';
import JourneyInfoSide from 'components/JourneyInfoSide';
import PodStatus from 'components/PodStatus';
import Documentation from 'components/Documentation';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'hooks/useAuth/useAuth';
import usePolling from 'hooks/usePolling/usePolling';
import useFetchData from 'hooks/useFetchData/useFetchData';
import { getJobDetail } from 'utils/network/http';
import config from 'utils/constants/config';
import { useParams, useHistory } from 'react-router-dom';
import { ROUTES } from 'utils/constants/routes';

const InTransitJobDetail = ({ className }) => {
  const { t } = useTranslation('InTransitJobDetail');
  const { profile } = useAuth();
  const { id } = useParams();
  let history = useHistory();
  const {
    loading,
    error,
    data: journey,
    fetchData,
    silentFetchData,
  } = useFetchData({
    fetchFunc: async () => getJobDetail(id),
  });
  const { startPolling } = usePolling({
    fetchFunc: silentFetchData,
    delay: config.FAST_POLLING_DELAY,
  });

  useEffect(() => {
    if (!journey && profile && !error && !loading) {
      fetchData();
      startPolling();
    }
  }, [journey, fetchData, profile, error, loading, startPolling]);

  useEffect(() => {
    if (journey?.status === 'DONE_WITHOUT_MANIFEST') {
      history.push(`${ROUTES.PENDING_PODS_DETAIL.DEFAULT}/${journey.id}`);
    }
  }, [journey, history]);

  const renderJob = useCallback(() => {
    if (loading) {
      return (
        <div className="in-transit-job-detail__loading" data-testid="loading">
          <Spinner />
        </div>
      );
    }

    if (error) {
      return (
        <div className="in-transit-job-detail__error" data-testid="error">
          <span>{t('error')}</span>
        </div>
      );
    }

    if (journey)
      return (
        <>
          <SideBar backLabel={t('back')} redirectRoute={ROUTES.IN_TRANSIT}>
            <JourneyInfoSide journey={journey} />
            <Assignments
              driverId={journey.driver_id}
              vehiclesIds={journey.vehicles_id}
              driverConfirmed={journey.driver_confirmed}
              showAtSide
              withoutTag
            />
            <PodStatus
              journeyId={journey.id}
              stops={journey.stops}
              full_status={journey.full_status}
              refetchData={silentFetchData}
              journeyStatus={journey.status}
            />
            <Documentation
              journeyId={journey.id}
              journeyStatus={journey.status}
            />
          </SideBar>
          <div className="in-transit-job-detail__body">
            <StopsDetails
              expanded
              withStatus
              showAdvanceButton
              journey={journey}
              refetchData={silentFetchData}
            />
          </div>
        </>
      );
  }, [journey, error, loading, t, silentFetchData]);

  return (
    <div className={className}>
      <div
        className="in-transit-job-detail"
        data-testid="in-transit-job-detail"
      >
        {renderJob()}
      </div>
    </div>
  );
};

export default InTransitJobDetail;
